<section class="speech-page">
  <header class="hero">
    <div>
      <p class="eyebrow">R&amp;D · Low-resource speech lab</p>
      <h1>Pashto &amp; Dari STT / TTS</h1>
      <p>Test Pashto speech recognition and VITS2 synthesis, listen to results, and review every run saved in the database.</p>
    </div>
    <div class="service-state" [class.ready]="status?.configured && status?.serviceReachable">
      <span></span>
      {{ statusLoading ? 'Checking…' : (status?.configured && status?.serviceReachable ? 'Service ready' : (status?.configured ? 'Service offline' : 'Setup required')) }}
      <small>{{ status?.mode || 'unknown' }} mode</small>
    </div>
  </header>

  <div class="notice" *ngIf="status && (!status.configured || !status.serviceReachable)">
    {{ status.configured ? 'The configured Pashto/Dari model service is not reachable.' : 'The speech adapter is not fully configured. Set the Pashto/Dari service environment variables on the GRMIS3 backend.' }}
  </div>
  <div class="notice" *ngIf="status?.configured && status?.serviceReachable">
    Pashto STT uses the public compatible model <strong>{{ sttModelLabel }}</strong>; Pashto TTS uses <strong>{{ ttsModelLabel }}</strong> with the requested repository's VITS2 code. The repository's exact STT weights and Dari checkpoint were not published.
  </div>
  <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>

  <div class="language-switch" role="group" aria-label="Language">
    <button type="button" [class.active]="language === 'pashto'" (click)="setLanguage('pashto')">
      <strong>پښتو</strong><span>Pashto</span>
    </button>
    <button type="button" [class.active]="language === 'dari'" [disabled]="!dariAvailable" (click)="setLanguage('dari')" [title]="dariAvailable ? 'Use Dari models' : 'The requested repository does not publish its Dari model checkpoint'">
      <strong>دری</strong><span>{{ dariAvailable ? 'Dari' : 'Dari · unavailable' }}</span>
    </button>
  </div>

  <div class="lab-grid">
    <article class="panel">
      <div class="panel-heading">
        <span class="step">01</span>
        <div><h2>Speech to text</h2><p>Record with the microphone or upload an audio file.</p></div>
      </div>

      <div class="recorder" [class.live]="state === 'recording'">
        <div class="timer">{{ durationLabel }}</div>
        <div class="bars"><i></i><i></i><i></i><i></i><i></i><i></i></div>
        <div class="button-row">
          <button class="primary" type="button" [disabled]="state === 'recording' || state === 'running'" (click)="startRecording()">
            <span class="material-icons">mic</span> Record
          </button>
          <button class="stop" type="button" [disabled]="state !== 'recording'" (click)="stopRecording()">
            <span class="material-icons">stop</span> Stop
          </button>
          <label class="upload" [class.disabled]="state === 'recording' || state === 'running'">
            <span class="material-icons">upload_file</span> Upload
            <input type="file" accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a,.webm" (change)="chooseFile($event)" [disabled]="state === 'recording' || state === 'running'">
          </label>
        </div>
      </div>

      <div class="audio-preview" *ngIf="sourceAudioUrl">
        <span>{{ selectedFile?.name || 'Microphone recording' }}</span>
        <audio [src]="sourceAudioUrl" controls></audio>
      </div>

      <button class="run" type="button" [disabled]="!canTranscribe" (click)="runStt()">
        <span class="material-icons">graphic_eq</span>
        {{ state === 'running' ? 'Transcribing…' : 'Run STT test' }}
      </button>

      <div class="result" *ngIf="transcription || sttResult">
        <div class="result-meta">
          <span>{{ sttResult?.latencyMs || 0 }} ms</span>
          <span>Language confidence {{ percent(sttResult?.languageProbability) }}</span>
        </div>
        <label>Transcription</label>
        <p dir="rtl">{{ transcription || 'No text returned.' }}</p>
      </div>
    </article>

    <article class="panel">
      <div class="panel-heading">
        <span class="step">02</span>
        <div><h2>Text to speech</h2><p>Generate a WAV sample with VITS2 controls.</p></div>
      </div>

      <label class="field-label" for="ttsText">{{ language === 'pashto' ? 'Pashto text' : 'Dari text' }}</label>
      <textarea id="ttsText" [(ngModel)]="ttsText" maxlength="2000" rows="6" dir="rtl"></textarea>
      <div class="char-count">{{ ttsText.length }} / 2,000</div>

      <div class="controls">
        <label>Expressiveness <strong>{{ noiseScale }}</strong><input type="range" min="0" max="1" step="0.1" [(ngModel)]="noiseScale"></label>
        <label>Duration variation <strong>{{ noiseScaleW }}</strong><input type="range" min="0" max="1" step="0.1" [(ngModel)]="noiseScaleW"></label>
        <label>Length / speed <strong>{{ lengthScale }}</strong><input type="range" min="0.5" max="2" step="0.1" [(ngModel)]="lengthScale"></label>
      </div>

      <button class="run" type="button" [disabled]="ttsRunning || !ttsText.trim()" (click)="runTts()">
        <span class="material-icons">volume_up</span>
        {{ ttsRunning ? 'Generating…' : 'Generate speech' }}
      </button>

      <div class="result" *ngIf="generatedAudioUrl || ttsResult">
        <div class="result-meta"><span>{{ ttsResult?.latencyMs || 0 }} ms</span><span>{{ bytes(ttsResult?.outputSizeBytes) }}</span></div>
        <label>Generated audio</label>
        <audio *ngIf="generatedAudioUrl" [src]="generatedAudioUrl" controls autoplay></audio>
      </div>
    </article>
  </div>

  <article class="panel history">
    <div class="history-head">
      <div><p class="eyebrow">Database log</p><h2>Test history</h2></div>
      <div class="history-actions">
        <select [(ngModel)]="logFilter" aria-label="Filter logs"><option value="ALL">All runs</option><option value="STT">STT only</option><option value="TTS">TTS only</option></select>
        <button type="button" (click)="loadLogs()" [disabled]="logsLoading"><span class="material-icons">refresh</span> Refresh</button>
      </div>
    </div>

    <div class="empty" *ngIf="!logsLoading && !filteredLogs.length">No Pashto/Dari speech tests have been logged yet.</div>
    <div class="table-wrap" *ngIf="filteredLogs.length">
      <table>
        <thead><tr><th>Run</th><th>Input</th><th>Result</th><th>Performance</th><th>Audio</th><th></th></tr></thead>
        <tbody>
          <ng-container *ngFor="let log of filteredLogs">
            <tr>
              <td><span class="kind" [class.tts]="log.operation === 'TTS'">{{ log.operation }}</span><strong>{{ log.language | titlecase }}</strong><small>{{ date(log.createdAt) }}</small></td>
              <td class="copy" [attr.dir]="log.inputText ? 'rtl' : null">{{ log.inputText || log.sourceFileName || 'Audio input' }}<small *ngIf="log.sourceSizeBytes">{{ bytes(log.sourceSizeBytes) }}</small></td>
              <td class="copy" [attr.dir]="log.outputText ? 'rtl' : null"><span class="status" [class.failed]="log.status === 'FAILED'">{{ log.status }}</span>{{ log.outputText || log.errorMessage || (log.operation === 'TTS' ? 'WAV generated' : '—') }}</td>
              <td><strong>{{ log.latencyMs || 0 }} ms</strong><small *ngIf="log.languageProbability != null">Confidence {{ percent(log.languageProbability) }}</small><small *ngIf="log.audioDurationSeconds">Audio {{ log.audioDurationSeconds }}s</small></td>
              <td class="audio-actions">
                <button *ngIf="log.operation === 'STT'" type="button" (click)="toggleAudio(log, 'input')"><span class="material-icons">{{ log.inputAudioUrl ? 'close' : 'play_arrow' }}</span>Source</button>
                <button *ngIf="log.operation === 'TTS' && log.status === 'COMPLETED'" type="button" (click)="toggleAudio(log, 'output')"><span class="material-icons">{{ log.outputAudioUrl ? 'close' : 'volume_up' }}</span>Output</button>
              </td>
              <td class="row-actions"><button title="Details" (click)="log.expanded = !log.expanded"><span class="material-icons">code</span></button><button title="Delete" class="delete" (click)="deleteLog(log)"><span class="material-icons">delete</span></button></td>
            </tr>
            <tr class="player-row" *ngIf="log.inputAudioUrl || log.outputAudioUrl"><td colspan="6"><audio *ngIf="log.inputAudioUrl" [src]="log.inputAudioUrl" controls></audio><audio *ngIf="log.outputAudioUrl" [src]="log.outputAudioUrl" controls></audio></td></tr>
            <tr class="detail-row" *ngIf="log.expanded"><td colspan="6"><div><label>Endpoint</label><code>{{ log.endpoint }}</code></div><div><label>Request</label><pre>{{ pretty(log.requestPayload) }}</pre></div><div><label>Response</label><pre>{{ pretty(log.responsePayload) }}</pre></div></td></tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </article>
</section>
